import { BenefitCodeEnum } from "@vitable/asclepius-api-client-ts"
import { centsToUsd, formatDollarsTrimmed } from "@common/utilities/dev-utils"
import { ENROLLMENT_BENEFIT_STEP_ORDER, VPC_STEP } from "@common/utilities/benefit-utils"
import { yearFromIso } from "@common/utilities/time-utils"
import {
  buildEnrollmentSegments,
  findBenefitStepEditRoute,
  type EnrollmentSegment
} from "@/modules/enrollment-new/enrollment-segments"
import { selectBenefitElectionLabel } from "@/modules/enrollment-new/enrollment-flow-helpers"
import type {
  BenefitElectionDependent,
  BenefitElectionsPackage,
  EnrollmentBenefitStep,
  NonVpcBenefitElection,
  TranslatableText
} from "@/modules/enrollment-new/enrollment-flow-types"
import type { NonVpcElectionSummary } from "@/modules/enrollment-new/stores/enrollment-new-store"

export type BenefitSummaryRowStatus = "selected" | "waived" | "free"

export interface BenefitSummaryRowData {
  key: string
  label: TranslatableText
  detail: TranslatableText | null
  status: BenefitSummaryRowStatus
  monthlyCostCents: number | null
  editRouteName: string | null
  editRouteParams: Record<string, string | number> | null
  editRouteQuery?: Record<string, string | number> | null
  benefitCode: BenefitCodeEnum
}

export interface BuildSummaryRowsInput {
  electionsPackage: BenefitElectionsPackage
  dependents: Array<BenefitElectionDependent>
  availableBenefitTypes: Array<EnrollmentBenefitStep>
  electionsByType: Record<string, Array<NonVpcBenefitElection>>
  hasMultipleYears: boolean
  orderedElectionsByStep: Partial<Record<EnrollmentBenefitStep, Array<NonVpcBenefitElection>>>
  summariesByElectionId: Record<string, NonVpcElectionSummary>
  medicareEligibleIn: boolean
}

export function buildSummaryRows(input: BuildSummaryRowsInput): Array<BenefitSummaryRowData> {
  const segments = buildEnrollmentSegments({
    availableBenefitTypes: input.availableBenefitTypes,
    electionsByType: input.electionsByType,
    vpcElection: input.electionsPackage.vpcElection,
    query: {},
    medicareEligibleIn: input.medicareEligibleIn
  })

  const rows: Array<BenefitSummaryRowData> = []

  for (const step of ENROLLMENT_BENEFIT_STEP_ORDER) {
    if (step === VPC_STEP) {
      const vpcRow = buildVpcRow(input.electionsPackage, input.dependents, segments)
      if (vpcRow) rows.push(vpcRow)
      continue
    }

    for (const election of input.orderedElectionsByStep[step] ?? []) {
      rows.push(
        buildNonVpcRow({
          election,
          step,
          segments,
          summariesByElectionId: input.summariesByElectionId,
          hasMultipleYears: input.hasMultipleYears
        })
      )
    }
  }

  return rows
}

function buildVpcRow(
  electionsPackage: BenefitElectionsPackage,
  dependents: Array<BenefitElectionDependent>,
  segments: ReadonlyArray<EnrollmentSegment>
): BenefitSummaryRowData | null {
  const vpc = electionsPackage.vpcElection
  if (!vpc) return null

  const employeeCostCents = vpc.vpcTierCost.cost ?? 0
  const dependentCostCents = vpc.vpcTierCost.cost_per_dependent ?? 0
  const waivedDependentsIds = vpc.waivedDependentsIds ?? []
  const enrolledDependentsCount = dependents.filter((d) => !waivedDependentsIds.includes(d.legalDependentId)).length
  const totalCostCents = employeeCostCents + dependentCostCents * enrolledDependentsCount

  const status: BenefitSummaryRowStatus = vpc.waived ? "waived" : totalCostCents === 0 ? "free" : "selected"
  const detail: TranslatableText | null = vpc.waived
    ? null
    : buildEmployeeAndDependentsDetail({
        employeeCostCents,
        dependentCostCents,
        count: enrolledDependentsCount,
        isIncluded: totalCostCents === 0
      })

  return {
    key: `vpc:${vpc.enrollment.id}`,
    label: { key: "Vitable Primary Care" },
    detail,
    status,
    monthlyCostCents: vpc.waived || totalCostCents === 0 ? null : totalCostCents,
    editRouteName: segments.find((s) => s.barGroupKey === VPC_STEP)?.route.name ?? null,
    editRouteParams: null,
    benefitCode: vpc.enrollment.benefit_code
  }
}

function buildNonVpcRow(args: {
  election: NonVpcBenefitElection
  step: EnrollmentBenefitStep
  segments: ReadonlyArray<EnrollmentSegment>
  summariesByElectionId: Record<string, NonVpcElectionSummary>
  hasMultipleYears: boolean
}): BenefitSummaryRowData {
  const { election, step, segments, summariesByElectionId, hasMultipleYears } = args
  const enrollment = election.enrollment
  const summary = summariesByElectionId[enrollment.id]
  const year = yearFromIso(enrollment.plan_year_coverage_start)
  const editRoute = findBenefitStepEditRoute(segments, step, year, election)
  const editQuery = editRoute?.query as Record<string, string | number> | undefined

  return {
    key: `non-vpc:${enrollment.id}`,
    label: labelText(selectBenefitElectionLabel(election), hasMultipleYears ? year : undefined),
    detail: buildNonVpcRowDetail(summary),
    status: summary.status,
    monthlyCostCents: summary.monthlyCostCents,
    editRouteName: editRoute?.name ?? null,
    editRouteParams: editRoute?.params ?? null,
    editRouteQuery: editQuery && Object.keys(editQuery).length > 0 ? editQuery : null,
    benefitCode: enrollment.benefit_code
  }
}

function labelText(name: string, year: number | undefined): TranslatableText {
  if (year !== undefined) {
    return { key: "{year}: {name}", params: { year: String(year), name } }
  }
  return { literal: name }
}

function buildNonVpcRowDetail(summary: NonVpcElectionSummary): TranslatableText | null {
  if (summary.detailData?.kind === "internal-breakdown") {
    return buildEmployeeAndDependentsDetail({
      employeeCostCents: summary.detailData.employeeCostCents,
      dependentCostCents: summary.detailData.dependentCostCents,
      count: summary.detailData.enrolledDependentsCount,
      isIncluded: false
    })
  }
  if (summary.detailData?.kind === "family-shape") {
    return buildFamilyShapeDetail(summary.detailData.enrolledDependentsCount)
  }
  return summary.planName ? { literal: summary.planName } : null
}

function buildFamilyShapeDetail(count: number): TranslatableText {
  if (count === 0) return { key: "Employee only" }
  if (count === 1) return { key: "Employee + 1 dependent" }
  return { key: "Employee + {count} dependents", params: { count } }
}

function buildEmployeeAndDependentsDetail(opts: {
  employeeCostCents: number
  dependentCostCents: number
  count: number
  isIncluded: boolean
}): TranslatableText {
  const employeeUsd = formatDollarsTrimmed(centsToUsd(opts.employeeCostCents))
  const dependentUsd = formatDollarsTrimmed(centsToUsd(opts.dependentCostCents))

  if (opts.isIncluded) {
    if (opts.count === 0) return { key: "Employee only (Included with enrollment)." }
    if (opts.count === 1) return { key: "Employee + 1 dependent (Included with enrollment)." }
    return { key: "Employee + {count} dependents (Included with enrollment).", params: { count: opts.count } }
  }

  if (opts.count === 0) {
    return { key: "Employee × {employee}/mo", params: { employee: employeeUsd } }
  }

  if (opts.employeeCostCents === opts.dependentCostCents) {
    if (opts.count === 1) {
      return { key: "Employee + 1 dependent × {perPerson}/person", params: { perPerson: employeeUsd } }
    }
    return {
      key: "Employee + {count} dependents × {perPerson}/person",
      params: { perPerson: employeeUsd, count: opts.count }
    }
  }

  if (opts.count === 1) {
    return {
      key: "Employee × {employee}/mo and 1 dependent × {dependent}/mo",
      params: { employee: employeeUsd, dependent: dependentUsd }
    }
  }
  return {
    key: "Employee × {employee}/mo and {count} dependents × {dependent}/mo",
    params: { employee: employeeUsd, dependent: dependentUsd, count: opts.count }
  }
}
